'use client';

import classNames from 'classnames';
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';

import { PropsWithClassname } from '@/app/types';

import Link from 'next/link';
import ChevronIcon from '@/app/components/ChevronIcon/ChevronIcon';
import Button, { ButtonHTMLTag } from '@/app/components/Button/Button';
import { Routes } from '@/app/enums';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '@/app/components/MainNavigation/MainNavigation';
import { User, UserRolesEnum } from '@/app/api';

interface DrawerNavigationProps extends PropsWithClassname {
  isOpen: boolean;
  menuItems: MenuItem[];
  currentUser?: User;
}

interface DrawerSubNavProps extends PropsWithClassname {
  isOpen: boolean;
  description?: string;
  subMenuItems: MenuItem['dropdownItems'];
}

const DrawerSubNav: FunctionComponent<DrawerSubNavProps> = ({ isOpen, subMenuItems, description, className }) => {
  const subNavContentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  return (
    <div
      className={classNames(className, 'overflow-hidden transition-all ml-[4px]')}
      style={{
        height: isOpen ? `${subNavContentRef.current?.getBoundingClientRect?.()?.height}px` : '0',
      }}
    >
      <div ref={subNavContentRef}>
        {!!description && <div className="mb-[8px] text-neutral-mid text-[14px]">{description}</div>}
        <ul className="pl-[20px] pb-[16px]">
          {subMenuItems?.map((subMenuItem) => (
            <li key={subMenuItem.titleTranslationKey}>
              {!!subMenuItem.path && (
                <Link
                  href={subMenuItem.path}
                  prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                  className="block py-[12px] hover:text-primary-mid"
                >
                  {t(subMenuItem.titleTranslationKey)}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const DrawerNavigation: FunctionComponent<DrawerNavigationProps> = ({ isOpen, menuItems, currentUser, className }) => {
  const { t } = useTranslation();

  const [isOverflowContainerVisible, setIsOverflowContainerVisible] = useState(false);
  const [openSubNavIndices, setOpenSubNavIndices] = useState<number[] | null>(null);

  const handleToggleOpenSubNavIndex = useCallback(
    (index: number) => {
      setOpenSubNavIndices((currentIndices) => {
        if (currentIndices?.includes(index)) {
          return currentIndices.filter((item) => item !== index);
        }

        return [...(currentIndices ?? []), index];
      });
    },
    [setOpenSubNavIndices],
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsOverflowContainerVisible(true);
    } else {
      document.body.style.overflow = 'auto';
      setTimeout(() => {
        setIsOverflowContainerVisible(false);
      }, 200);
    }
  }, [isOpen]);

  return (
    <div
      className={classNames('drawer-nav-top-position overflow-hidden absolute bottom-0 z-30', {
        'w-full left-0 right-0': isOpen || isOverflowContainerVisible,
      })}
    >
      <div
        className={classNames(
          className,
          'absolute xl:hidden w-full h-full transition-all bg-secondary-light px-[24px]',
          {
            'right-[-100%]': !isOpen,
            'right-0': isOpen,
          },
        )}
      >
        <ul>
          {menuItems.map((menuItem, index) => (
            <li key={index} className="relative">
              <Link
                className={classNames('py-[12px] hover:text-primary-mid flex justify-between items-center', {
                  'text-primary-mid': openSubNavIndices && openSubNavIndices[openSubNavIndices.length - 1] === index,
                  'font-medium': openSubNavIndices?.includes(index),
                })}
                href={menuItem.path ?? '#'}
                onClick={menuItem.dropdownItems ? () => handleToggleOpenSubNavIndex(index) : undefined}
                prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
              >
                <span>{t(menuItem.titleTranslationKey)}</span>
                {!!menuItem.dropdownItems && (
                  <ChevronIcon
                    className={classNames('ml-[8px]', {
                      'rotate-180': openSubNavIndices?.includes(index),
                    })}
                  />
                )}
              </Link>
              {!!menuItem.dropdownItems && (
                <DrawerSubNav
                  isOpen={openSubNavIndices?.includes(index) ?? false}
                  subMenuItems={menuItem.dropdownItems}
                  description={menuItem.descriptionTranslationKey ? t(menuItem.descriptionTranslationKey) : undefined}
                />
              )}
            </li>
          ))}
          {!currentUser && (
            <li className="relative">
              <Link
                prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                className="py-[12px] hover:text-primary-mid flex justify-between items-center"
                href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.LOGIN}`}
              >
                {t('components.Header.signIn')}
              </Link>
            </li>
          )}
          {!!currentUser && (
            <li className="relative border-t-neutral-light border-t pt-[4px] mt-[4px]">
              <Link
                prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                className="block py-[12px] hover:text-primary-mid"
                href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.MY_DONIO}`}
              >
                <span className="block font-medium">{currentUser.email}</span>
                <span className="block">{t('components.Header.myDonio')}</span>
              </Link>
            </li>
          )}
          {!!currentUser?.roles?.includes(UserRolesEnum.Admin) && (
            <li className="relative">
              <Link
                prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                className="py-[12px] hover:text-primary-mid flex justify-between items-center"
                href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.ADMIN}`}
              >
                {t('components.Header.admin')}
              </Link>
            </li>
          )}
          <li className="relative flex flex-col items-stretch mt-[20px]">
            <Button
              className="text-center"
              tagSpecificProps={{
                htmlTag: ButtonHTMLTag.LINK,
                href: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.SUGGESTION}`,
              }}
            >
              {t('components.Header.startBrowsing')}
            </Button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DrawerNavigation;
